import {
	AdTargetingOptions,
	IDefinedSlot,
	ILazyLoadParams,
	IPubAds,
	ISizeMapping,
	PubAdsMethods
} from '../models/banner/IGoogleTag';

export default class AdMethodHandler {
	public static displayContainer(containerId: string): void {
		if (!window.googletag?.display) {
			return;
		}

		window.googletag.display(containerId);
	}

	public static cmdPush(callback: () => void): void {
		if (!window.googletag?.cmd?.push) {
			return;
		}

		window.googletag.cmd.push(callback);
	}

	public static enableServices(): void {
		if (!window.googletag?.enableServices) {
			return;
		}

		window.googletag.enableServices();
	}

	public static defineSlot(code: string, dimensions: [number, number], containerId: string): IDefinedSlot | undefined {
		if (!window.googletag?.defineSlot) {
			return;
		}

		return window.googletag.defineSlot(code, dimensions, containerId);
	}

	public static addServiceToDefinedSlot(slot?: IDefinedSlot): IDefinedSlot | undefined {
		const pubads = AdMethodHandler.retrievePubadsService();
		if (!pubads) {
			return;
		}

		if (!slot?.addService) {
			return;
		}

		slot.addService(pubads);
		return slot;
	}

	public static addSizeMappingToSlot(slot: IDefinedSlot | undefined, sizeMapping: ISizeMapping | undefined): void {
		if (!slot?.defineSizeMapping) {
			return;
		}

		if (!sizeMapping) {
			return;
		}

		slot.defineSizeMapping(sizeMapping);
	}

	public static retrieveSizeMappingBuilder(): ISizeMapping | undefined {
		if (!window.googletag?.sizeMapping) {
			return;
		}

		return window.googletag.sizeMapping();
	}

	public static buildSizeMapping(sizeMapping: ISizeMapping | undefined): ISizeMapping | undefined {
		if (!sizeMapping) {
			return;
		}

		if (!sizeMapping.build) {
			return;
		}

		return sizeMapping.build();
	}

	public static addSizeToSizeMapping(
		sizeMapping: ISizeMapping | undefined,
		viewportSize: [number, number],
		slotSize: [number, number][]
	): ISizeMapping | undefined {
		if (!sizeMapping) {
			return;
		}

		if (!sizeMapping.addSize) {
			return;
		}

		return sizeMapping.addSize(viewportSize, slotSize);
	}

	public static retrievePubadsService(): IPubAds | undefined {
		if (!window.googletag?.pubads) {
			return;
		}

		return window.googletag.pubads();
	}

	public static callPubadsMethodWithoutParameters(
		methodName:
			| PubAdsMethods.COLLAPSE_EMPTY_DIVS
			| PubAdsMethods.DISABLE_INITIAL_LOAD
			| PubAdsMethods.ENABLE_SINGLE_REQUEST
	): void {
		const pubads = AdMethodHandler.retrievePubadsService();
		if (!pubads) {
			return;
		}

		if (!pubads[methodName]) {
			return;
		}

		// eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
		pubads[methodName]();
	}

	public static callPubadsEnableLazyLoadMethod(params: ILazyLoadParams): void {
		const pubads = AdMethodHandler.retrievePubadsService();
		if (!pubads) {
			return;
		}

		if (!pubads.enableLazyLoad) {
			return;
		}

		pubads.enableLazyLoad(params);
	}

	public static callPubadsSetTargetingMethod(key: AdTargetingOptions, value: string | string[]): void {
		const pubads = AdMethodHandler.retrievePubadsService();
		if (!pubads) {
			return;
		}

		if (!pubads.setTargeting) {
			return;
		}

		pubads.setTargeting(key, value);
	}

	public static callPubadsGetTargetingMethod(key: AdTargetingOptions): string[] | undefined {
		const pubads = AdMethodHandler.retrievePubadsService();
		if (!pubads) {
			return;
		}

		if (!pubads.getTargeting) {
			return;
		}

		return pubads.getTargeting(key);
	}

	public static callPubadsClearTargetingMethod(key: AdTargetingOptions): void {
		const pubads = AdMethodHandler.retrievePubadsService();
		if (!pubads) {
			return;
		}

		if (!pubads.clearTargeting) {
			return;
		}

		pubads.clearTargeting(key);
	}

	public static callPubadsRefreshMethod(slots: IDefinedSlot[]): void {
		const pubads = AdMethodHandler.retrievePubadsService();
		if (!pubads) {
			return;
		}

		if (!pubads.refresh) {
			return;
		}

		return pubads.refresh(slots);
	}
}
